import { useEffect, useMemo, useState } from "react";
import { useSession } from "./session";

const STORAGE_PREFIX = "gohubCommunityFavorites";

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const normalizeCommunity = (community) => {
  if (!community || typeof community !== "object") {
    return null;
  }

  const name = firstText(
    community.people,
    community.name,
    community.label,
    community.community,
    community.value
  );

  const url = firstText(community.url);
  const flag = firstText(community.flag);

  if (!name && !url) {
    return null;
  }

  const id = (url || name).trim().toLowerCase();

  return {
    id,
    people: name || "Community",
    flag,
    url,
  };
};

const storageKeyFor = (email) => {
  const normalized = String(email || "").trim().toLowerCase();

  return normalized
    ? `${STORAGE_PREFIX}:${encodeURIComponent(normalized)}`
    : "";
};

const readFavorites = (email) => {
  if (typeof window === "undefined") {
    return [];
  }

  const key = storageKeyFor(email);

  if (!key) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(key);

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    const deduped = new Map();

    parsed.forEach((item) => {
      const normalized = normalizeCommunity(item);

      if (normalized) {
        deduped.set(normalized.id, normalized);
      }
    });

    return Array.from(deduped.values());
  } catch {
    return [];
  }
};

const writeFavorites = (email, favorites) => {
  if (typeof window === "undefined") {
    return;
  }

  const key = storageKeyFor(email);

  if (!key) {
    return;
  }

  window.localStorage.setItem(
    key,
    JSON.stringify(favorites)
  );

  window.dispatchEvent(
    new Event("gohub-community-favorites")
  );
};

export function useCommunityFavorites() {
  const user = useSession();
  const email = user?.email || "";

  const [favorites, setFavorites] = useState(() =>
    readFavorites(email)
  );

  useEffect(() => {
    const refresh = () => {
      setFavorites(readFavorites(email));
    };

    refresh();

    window.addEventListener(
      "gohub-community-favorites",
      refresh
    );

    window.addEventListener(
      "storage",
      refresh
    );

    return () => {
      window.removeEventListener(
        "gohub-community-favorites",
        refresh
      );

      window.removeEventListener(
        "storage",
        refresh
      );
    };
  }, [email]);

  const favoriteIds = useMemo(
    () => new Set(favorites.map((item) => item.id)),
    [favorites]
  );

  const isCommunitySaved = (community) => {
    const normalized = normalizeCommunity(community);

    return Boolean(
      normalized &&
        favoriteIds.has(normalized.id)
    );
  };

  const toggleCommunityFavorite = (community) => {
    if (!email) {
      return {
        saved: false,
        needsSignIn: true,
      };
    }

    const normalized = normalizeCommunity(community);

    if (!normalized) {
      return {
        saved: false,
        needsSignIn: false,
      };
    }

    const current = readFavorites(email);
    const exists = current.some(
      (item) => item.id === normalized.id
    );

    const next = exists
      ? current.filter(
          (item) => item.id !== normalized.id
        )
      : [...current, normalized];

    writeFavorites(email, next);
    setFavorites(next);

    return {
      saved: !exists,
      needsSignIn: false,
    };
  };

  const removeCommunityFavorite = (community) => {
    if (!email) {
      return;
    }

    const normalized = normalizeCommunity(community);

    if (!normalized) {
      return;
    }

    const next = readFavorites(email).filter(
      (item) => item.id !== normalized.id
    );

    writeFavorites(email, next);
    setFavorites(next);
  };

  return {
    favorites,
    favoriteCount: favorites.length,
    isCommunitySaved,
    toggleCommunityFavorite,
    removeCommunityFavorite,
    isSignedIn: Boolean(email),
  };
}
